import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import {
  type CheckDepositBatch,
  type UnifiedTransfer,
  transferAchBatchGetBatchOptions,
  transferCheckDepositbatchGetBatchOptions,
  transferCheckDepositbatchUpdateBatchMutation,
  transferSearchTransfersOptions,
} from '@column/api';
import { Chip, Icon, Input, Button as KitButton, Tooltip, formatNumber } from '@column/column-ui-kit';
import {
  Button,
  DataTable,
  ScrollArea,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  cn,
} from '@column/ui';

import { CopyInput } from '~/components/CopyInput';
import { Loading } from '~/components/Loading';
import { BankAccountCard } from '~/components/ObjectCards/BankAccountCard';
import { RenderFields } from '~/components/RenderFields';
import { SectionHeader } from '~/components/SectionHeader';
import { tableDefaults } from '~/components/Table/defaults';
import { Empty as TableEmpty } from '~/components/Table/Empty';
import { Pagination as TablePagination } from '~/components/Table/Pagination';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { useNavigate } from '~/lib/navigation';
import { pickACHBatchAmounts } from '~/lib/transfers/achBatch';
import { TransferStatusChip } from '~/lib/transfers/components/TransferStatusChip';
import { getTransferDirectionIcon, getTransferDirectionLabel } from '~/lib/transfers/direction';
import { useBatchSheet } from '~/lib/transfers/hooks/useBatchSheet';
import { transferTableColumns } from '~/lib/transfers/table';
import { formatTransferType, getBatchNetAmount, getTransferURL } from '~/lib/transfers/utils';
import { TransferEntryType } from '~/repositories';
import { TableTimeFormat } from '~/repositories/UserRepository';
import { useTableConfig } from '~/stores/Config';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { ACHTransferBatch } from '~/typings/API';
import { formatDateTime, keysToCamelCase } from '~/util';

const MEMBER_COLUMN_ACCESSORS = new Set(['description', 'status', 'created_at']);

const STATEMENT_GROUPING: Record<string, { label: string; description: string }> = {
  individual: { label: 'Individual', description: 'List each transfer separately on the statement.' },
  rolled_up: { label: 'Rolled up', description: 'Combine into one credit and one debit line per day.' },
};

const POLL_MS = 5000;

const NON_TERMINAL_ACH_BATCH_STATUSES = new Set(['pending', 'validating', 'validated', 'processing']);

const REVEAL_DELAY_MS = 500;

const withScope = <T extends { queryKey: readonly unknown[] }>(options: T, scope: readonly unknown[]): T => ({
  ...options,
  queryKey: [...options.queryKey, ...scope] as unknown as T['queryKey'],
});

const checkBatchDetailOptions = (batchId: string, scope: readonly unknown[]) =>
  withScope(transferCheckDepositbatchGetBatchOptions({ path: { check_deposit_batch_id: batchId } }), scope);

const achBatchDetailOptions = (batchId: string, scope: readonly unknown[]) =>
  withScope(transferAchBatchGetBatchOptions({ path: { ach_transfer_batch_id: batchId } }), scope);

const batchMembersOptions = (
  batchId: string,
  pageSize: number,
  cursor: string | undefined,
  scope: readonly unknown[]
) =>
  withScope(
    transferSearchTransfersOptions({
      query: { transfer_batch_id: batchId, limit: pageSize, ...(cursor ? { starting_after: cursor } : {}) },
    }),
    scope
  );

const toUnifiedCheckBatch = (batch: CheckDepositBatch): UnifiedTransfer => ({
  id: batch.id,
  type: 'check_batch',
  amount: batch.total_amount,
  currency_code: batch.currency_code,
  description: batch.description ?? '',
  status: batch.status,
  is_incoming: true,
  batch_transfer_count: batch.total_count,
  idempotency_key: '',
  created_at: batch.created_at,
  updated_at: batch.updated_at,
  completed_at: null,
  settled_at: null,
});

const mutedIcon = (icon: ReactNode) => (
  <span className="inline-flex text-muted-foreground [&_svg]:[--icon-color:var(--color-muted-foreground)]">{icon}</span>
);

const BatchAmount: React.FC<{ value: number; currencyCode: string; label?: string }> = ({
  value,
  currencyCode,
  label,
}) => (
  <div>
    <div
      className={cn(
        'text-2xl font-semibold leading-7 tracking-[-0.02em]',
        value > 0 ? '[color:var(--color-seafoam-600)]' : 'text-foreground'
      )}
    >
      {`${value < 0 ? '-' : ''}${formatNumber(Math.abs(value), currencyCode)}`}
      <span className="ml-1 text-sm font-medium text-muted-foreground">{currencyCode}</span>
    </div>
    {label && <div className="mt-1 text-sm font-medium text-muted-foreground">{label}</div>}
  </div>
);

const BatchTransfersSheetBody: React.FC<{ batch: UnifiedTransfer }> = ({ batch }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const platformId = useSessionStore((s) => s.currentPlatform?.id);
  const isSandbox = useSessionStore((s) => s.isSandbox);
  const scope = [platformId, isSandbox];
  const formMethods = useForm({ defaultValues: { description: '' } });
  const { addDangerNotification, addSuccessNotification } = useNotificationStore();
  const { timeFormat, update: updateTableConfig } = useTableConfig('transfers');
  const { pageSize, update: updateBatchTableConfig } = useTableConfig('batchTransfers');
  const { cursor, hasPrevious, onNext, onPrevious, pageIndex, reset: resetPagination } = usePaginationState();

  const handlePageSizeChange = useCallback(
    (value: number) => {
      resetPagination();
      updateBatchTableConfig({ limit: value }).catch(() => {
        addDangerNotification({ content: 'Failed to save page size preference' });
      });
    },
    [resetPagination, updateBatchTableConfig, addDangerNotification]
  );

  const type = batch.type as TransferEntryType;

  const isPartialAccess = Boolean(batch.batch_partial_access);

  const checkBatchOptions = checkBatchDetailOptions(batch.id, scope);
  const checkBatchQuery = useQuery({
    ...checkBatchOptions,
    enabled: type === 'check_batch',
    staleTime: 15_000,
  });

  const achBatchQuery = useQuery({
    ...achBatchDetailOptions(batch.id, scope),
    enabled: type === 'ach_batch',
    refetchInterval: ({ state }) => (NON_TERMINAL_ACH_BATCH_STATUSES.has(state.data?.status ?? '') ? POLL_MS : false),
    staleTime: 15_000,
  });
  const checkBatch = checkBatchQuery.data;
  const achBatch = achBatchQuery.data;
  const detailQuery = type === 'ach_batch' ? achBatchQuery : checkBatchQuery;
  const isProcessing = NON_TERMINAL_ACH_BATCH_STATUSES.has(achBatch?.status ?? '');

  const {
    data,
    isLoading,
    isFetching,
    isError: isMembersError,
    refetch: refetchMembers,
  } = useQuery({
    ...batchMembersOptions(batch.id, pageSize, cursor, scope),
    placeholderData: keepPreviousData,
    refetchInterval: isProcessing ? POLL_MS : false,
    staleTime: 15_000,
  });

  const wasProcessingRef = useRef(isProcessing);
  useEffect(() => {
    if (wasProcessingRef.current && !isProcessing) {
      refetchMembers();
    }
    wasProcessingRef.current = isProcessing;
  }, [isProcessing, refetchMembers]);

  const achAmounts =
    achBatch && !isPartialAccess ? pickACHBatchAmounts(keysToCamelCase(achBatch) as ACHTransferBatch) : null;
  const detailStatus = achBatch?.status ?? checkBatch?.status ?? batch.status;
  const createdAt = achBatch?.created_at ?? checkBatch?.created_at;
  const summary = isPartialAccess
    ? `${batch.batch_transfer_count ?? 0} ${batch.batch_transfer_count === 1 ? 'Transfer' : 'Transfers'}`
    : achBatch
      ? [
          { label: 'Created', count: achBatch.succeeded_count },
          { label: 'Failed', count: achBatch.failed_count },
          { label: 'Invalid', count: achBatch.validation_failed_count },
        ]
          .filter((entry) => entry.count > 0)
          .map((entry) => `${entry.count} ${entry.label}`)
          .join(' · ')
      : checkBatch && `${checkBatch.total_count} ${checkBatch.total_count === 1 ? 'Check' : 'Checks'}`;
  const bankAccountId =
    type === 'ach_batch' ? achBatch?.import_config?.nacha?.bank_account_id : checkBatch?.bank_account_id;
  const statementGroupingMode = type === 'check_batch' ? 'rolled_up' : achBatch?.statement_grouping_mode;
  const statementGrouping = statementGroupingMode ? STATEMENT_GROUPING[statementGroupingMode] : undefined;

  const savedDescription = (type === 'ach_batch' ? achBatch?.description : checkBatch?.description) ?? '';
  const description = formMethods.watch('description');

  const lastSavedDescriptionRef = useRef('');
  // Stays false until the form holds the loaded description, so Save can't flash on open.
  const isDescriptionDirty =
    type === 'check_batch' && lastSavedDescriptionRef.current === savedDescription && description !== savedDescription;

  useEffect(() => {
    if (formMethods.getValues('description') === lastSavedDescriptionRef.current) {
      formMethods.reset({ description: savedDescription });
    }
    lastSavedDescriptionRef.current = savedDescription;
  }, [savedDescription, formMethods]);

  const { mutate: saveDescription, isPending: isSavingDescription } = useMutation({
    ...transferCheckDepositbatchUpdateBatchMutation(),
    onSuccess: (updated) => {
      queryClient.setQueryData(checkBatchOptions.queryKey, updated);
      queryClient.invalidateQueries({ queryKey: [{ _id: 'transferSearchTransfers' }] });
      addSuccessNotification({ content: 'Batch description updated' });
    },
    onError: (error) => {
      addDangerNotification({ content: error instanceof Error ? error.message : 'Failed to update description' });
    },
  });

  const members = useMemo(() => data?.transfers ?? [], [data]);
  const hasMore = data?.has_more ?? false;

  const handleTimeFormatChange = useCallback(
    (value: TableTimeFormat) => {
      updateTableConfig({ timeFormat: value }).catch(() => {
        addDangerNotification({ content: 'Failed to save time format preference' });
      });
    },
    [updateTableConfig, addDangerNotification]
  );

  const columns = useMemo(
    () =>
      transferTableColumns({
        timeFormat,
        onTimeFormatChange: handleTimeFormatChange,
        showBatchMembership: false,
      })
        .filter((column) => MEMBER_COLUMN_ACCESSORS.has(String(column.accessor)))
        .map((column) => (column.accessor === 'description' ? { ...column, width: 'minmax(160px, auto)' } : column)),
    [timeFormat, handleTimeFormatChange]
  );

  const handleNext = useCallback(() => {
    if (members.length > 0) {
      onNext({ cursor: members[members.length - 1].id });
    }
  }, [members, onNext]);

  return (
    <>
      <SheetHeader className="gap-0 border-b border-border px-5 pb-4 pt-4">
        <SheetTitle className="text-base font-medium">{formatTransferType(type)}</SheetTitle>
        <SheetDescription className="sr-only">Transfers in this batch</SheetDescription>
      </SheetHeader>

      <ScrollArea
        fadeEdges
        className="min-h-0 flex-1 !rounded-none [&>[data-slot=scroll-area-viewport]>div]:!block [&>[data-slot=scroll-area-viewport]]:focus-visible:outline-none [&>[data-slot=scroll-area-viewport]]:focus-visible:ring-0"
      >
        {!detailQuery.data ? (
          detailQuery.isError ? (
            <TableEmpty
              icon={<Icon.Warning />}
              title="Failed to load batch"
              description="Something went wrong while loading this batch."
              action={
                <Button size="sm" variant="secondary" onClick={() => detailQuery.refetch()}>
                  Retry
                </Button>
              }
            />
          ) : (
            <Loading isLoading />
          )
        ) : (
          <>
            <div className="border-b border-border px-5 pb-5 pt-4">
              {isPartialAccess ? (
                <BatchAmount value={getBatchNetAmount(batch)} currencyCode={batch.currency_code} />
              ) : type === 'check_batch' ? (
                <BatchAmount value={checkBatch?.total_amount ?? 0} currencyCode={batch.currency_code} />
              ) : achAmounts && (achAmounts.credit > 0 || achAmounts.debit > 0) ? (
                <div className="flex flex-wrap gap-x-8 gap-y-4">
                  {achAmounts.credit > 0 && (
                    <BatchAmount
                      value={-achAmounts.credit}
                      currencyCode={batch.currency_code}
                      label={achAmounts.debit > 0 ? 'Credit' : undefined}
                    />
                  )}
                  {achAmounts.credit > 0 && achAmounts.debit > 0 && <div className="w-px self-stretch bg-border" />}
                  {achAmounts.debit > 0 && (
                    <BatchAmount
                      value={achAmounts.debit}
                      currencyCode={batch.currency_code}
                      label={achAmounts.credit > 0 ? 'Debit' : undefined}
                    />
                  )}
                </div>
              ) : (
                <BatchAmount value={0} currencyCode={batch.currency_code} />
              )}
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                <TransferStatusChip status={detailStatus} transferType={type} size="large" />
                <Chip icon={mutedIcon(getTransferDirectionIcon(type, batch.is_incoming))} size="large" variant="subtle">
                  {getTransferDirectionLabel(type, batch.is_incoming)}
                </Chip>
                {summary && (
                  <Chip icon={mutedIcon(<Icon.Folder />)} size="large" variant="subtle">
                    {summary}
                  </Chip>
                )}
                {createdAt && (
                  <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-muted-foreground [&_svg]:[--icon-color:var(--color-muted-foreground)]">
                    <Icon.Calendar />
                    {formatDateTime(createdAt, { timeZoneFormat: 'local' })}
                  </span>
                )}
              </div>
              {batch.batch_partial_access && (
                <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground [&_svg]:[--icon-color:var(--color-muted-foreground)]">
                  <Icon.CircleInfo />
                  Totals include only the transfers you have access to.
                </div>
              )}
              <div className="mt-4">
                <FormProvider {...formMethods}>
                  <RenderFields
                    sections={[
                      {
                        variant: 'isCompact',
                        fields: [
                          {
                            id: 'description',
                            label: 'Description',
                            children: ({ onChange, value }) =>
                              type === 'check_batch' ? (
                                <div className="flex items-center">
                                  <div className="min-w-0 flex-1">
                                    <Input
                                      value={String(value ?? '')}
                                      onChange={onChange}
                                      placeholder="Add a description"
                                    />
                                  </div>
                                  <div
                                    className={cn(
                                      '-my-1 shrink-0 overflow-hidden py-1 transition-[max-width,opacity,margin,padding] duration-200 ease-out',
                                      isDescriptionDirty
                                        ? 'ml-1 max-w-28 px-1 opacity-100'
                                        : 'ml-0 max-w-0 px-0 opacity-0'
                                    )}
                                  >
                                    <KitButton
                                      size="default"
                                      isLoading={isSavingDescription}
                                      isDisabled={!isDescriptionDirty}
                                      onClick={() =>
                                        saveDescription({
                                          path: { check_deposit_batch_id: batch.id },
                                          body: { description },
                                        })
                                      }
                                    >
                                      Save
                                    </KitButton>
                                  </div>
                                </div>
                              ) : (
                                <CopyInput value={achBatch?.description || '-'} />
                              ),
                          },
                          {
                            id: 'batchId',
                            label: 'Batch ID',
                            children: () => <CopyInput value={batch.id} isMonospaced />,
                          },
                          {
                            id: 'bankAccount',
                            label: 'Bank Account',
                            hide: !bankAccountId,
                            children: () => <BankAccountCard bankAccountId={bankAccountId ?? ''} />,
                          },
                          {
                            id: 'importType',
                            label: 'Import Type',
                            hide: type !== 'ach_batch',
                            children: () => (
                              <div className="flex items-center @min-[464px]/form-field:h-[42px]">
                                <Chip
                                  size="large"
                                  icon={
                                    achBatch?.import_type === 'nacha' ? (
                                      <Icon.Nacha style={{ ['--icon-size' as string]: '16px' }} />
                                    ) : achBatch?.import_type === 'csv' ? (
                                      <Icon.Csv style={{ ['--icon-size' as string]: '16px' }} />
                                    ) : undefined
                                  }
                                >
                                  {achBatch?.import_type ? achBatch.import_type.toUpperCase() : '-'}
                                </Chip>
                              </div>
                            ),
                          },
                          {
                            id: 'statementGroupingMode',
                            label: 'Statement Grouping',
                            children: () => (
                              <div className="flex items-center gap-1.5 @min-[464px]/form-field:h-[42px]">
                                <Chip size="large">{statementGrouping?.label ?? (statementGroupingMode || '-')}</Chip>
                                {statementGrouping && (
                                  <Tooltip
                                    content={statementGrouping.description}
                                    placement="top"
                                    zIndex={100}
                                    className="flex items-center opacity-35 [&_svg]:[--icon-color:var(--color-muted-foreground)]"
                                  >
                                    <Icon.CircleQuestionmark />
                                  </Tooltip>
                                )}
                              </div>
                            ),
                          },
                        ],
                      },
                    ]}
                  />
                </FormProvider>
              </div>
            </div>

            <div className="px-5 pb-6 pt-5">
              <SectionHeader text="Transfers" flush className="!pt-0" />
              <div>
                <Loading isLoading={isLoading}>
                  <DataTable<UnifiedTransfer>
                    {...tableDefaults}
                    className={members.length === 0 ? 'min-h-[245px]' : undefined}
                    columns={columns}
                    data={members}
                    rowKey={(row) => row.id}
                    isLoading={isFetching && !isLoading}
                    emptyContent={
                      <TableEmpty
                        className="min-h-[200px]"
                        icon={<Icon.AnimationArrows />}
                        title={isMembersError ? 'Failed to load transfers' : 'No transfers yet'}
                        description={
                          isMembersError
                            ? 'Something went wrong while loading transfers.'
                            : 'No transfers were found in this batch.'
                        }
                        action={
                          isMembersError ? (
                            <Button size="sm" variant="secondary" onClick={() => refetchMembers()}>
                              Retry
                            </Button>
                          ) : undefined
                        }
                      />
                    }
                    onRowClick={(row) => navigate(getTransferURL(row.id, row.type as TransferEntryType))}
                  />
                  <TablePagination
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                    current={pageIndex + 1}
                    hasPrevious={hasPrevious}
                    hasNext={hasMore}
                    onPrevious={onPrevious}
                    onNext={handleNext}
                  />
                </Loading>
              </div>
            </div>
          </>
        )}
      </ScrollArea>

      {type === 'ach_batch' && (
        <SheetFooter className="mt-0 flex-row justify-end border-t border-border px-5 py-4">
          <Button size="sm" variant="secondary" onClick={() => navigate(getTransferURL(batch.id, type))}>
            View batch
            <Icon.ChevronRightLarge />
          </Button>
        </SheetFooter>
      )}
    </>
  );
};

export const BatchTransfersSheet: React.FC = () => {
  const platformId = useSessionStore((s) => s.currentPlatform?.id);
  const isSandbox = useSessionStore((s) => s.isSandbox);
  const { addDangerNotification } = useNotificationStore();
  const { batchId, closeBatchSheet } = useBatchSheet();
  const scope = [platformId, isSandbox];

  const { data, isError, isSuccess, isFetching } = useQuery({
    ...withScope(
      transferSearchTransfersOptions({ query: { transfer_id: batchId ?? '', show_batches: true, limit: 1 } }),
      scope
    ),
    enabled: Boolean(batchId && platformId),
    staleTime: 15_000,
  });

  const searchBatch = batchId ? (data?.transfers?.find((transfer) => transfer.id === batchId) ?? null) : null;
  const isSearchMiss = Boolean(batchId) && isSuccess && !isFetching && !searchBatch;

  const { data: checkBatch, status: checkBatchStatus } = useQuery({
    ...checkBatchDetailOptions(batchId ?? '', scope),
    enabled: isSearchMiss || searchBatch?.type === 'check_batch',
    retry: isSearchMiss ? false : undefined,
    staleTime: 15_000,
  });
  const fallbackBatch = useMemo(
    () => (checkBatch?.id === batchId ? toUnifiedCheckBatch(checkBatch) : null),
    [checkBatch, batchId]
  );

  const batch = searchBatch ?? fallbackBatch;
  const isMissing =
    Boolean(batchId) &&
    !isFetching &&
    (isError ||
      (isSearchMiss &&
        (checkBatchStatus === 'error' || (checkBatchStatus === 'success' && checkBatch?.id !== batchId))));

  useEffect(() => {
    if (!isMissing) return;
    addDangerNotification({ content: 'Batch not found' });
    closeBatchSheet();
  }, [isMissing, addDangerNotification, closeBatchSheet]);
  const { status: achBatchStatus } = useQuery({
    ...achBatchDetailOptions(batchId ?? '', scope),
    enabled: searchBatch?.type === 'ach_batch',
    staleTime: 15_000,
  });
  const { pageSize } = useTableConfig('batchTransfers');
  const { status: membersStatus } = useQuery({
    ...batchMembersOptions(batchId ?? '', pageSize, undefined, scope),
    enabled: Boolean(batch),
    staleTime: 15_000,
  });

  const isSettled = (status: string) => status === 'success' || status === 'error';
  const isReady =
    Boolean(batch) &&
    isSettled(batch?.type === 'ach_batch' ? achBatchStatus : checkBatchStatus) &&
    isSettled(membersStatus);

  // Hold the sheet closed until its data is ready, but never longer than REVEAL_DELAY_MS.
  const [isRevealed, setIsRevealed] = useState(false);
  useEffect(() => {
    if (!batchId) {
      setIsRevealed(false);
      return;
    }
    if (isRevealed) return;
    if (isReady) {
      setIsRevealed(true);
      return;
    }
    const timer = window.setTimeout(() => setIsRevealed(true), REVEAL_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [batchId, isReady, isRevealed]);
  const isOpen = Boolean(batchId) && (isRevealed || isReady);

  const shownRef = useRef<UnifiedTransfer | null>(null);
  if (batch) {
    shownRef.current = batch;
  }
  const shown = batch ?? (batchId ? null : shownRef.current);

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && closeBatchSheet()}>
      {(shown || isOpen) && (
        <SheetContent
          side="right"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            (event.currentTarget as HTMLElement | null)?.focus();
          }}
          className="flex w-full max-w-[600px] flex-col gap-0 border-l-0 p-0 shadow-[-8px_0_24px_rgba(0,0,0,0.12)] outline-none sm:max-w-[600px]"
        >
          {shown ? (
            <BatchTransfersSheetBody key={shown.id} batch={shown} />
          ) : (
            <>
              <SheetHeader className="gap-0 border-b border-border px-5 pb-4 pt-4">
                <SheetTitle className="text-base font-medium">Batch</SheetTitle>
                <SheetDescription className="sr-only">Transfers in this batch</SheetDescription>
              </SheetHeader>
              <Loading isLoading />
            </>
          )}
        </SheetContent>
      )}
    </Sheet>
  );
};
